import { useEffect, useState } from "react";
import { Avatar, Button, CircularProgress, Divider, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AiOutlineInfoCircle, AiOutlineHeart, AiFillHeart } from "react-icons/ai";
import verifiedSVG from "../../images/verifiedSVG.svg";
import { like } from "../../auth/like";
import { removeLike } from "../../auth/removeLike";
import { getInitialFeedData, getNextFeedData } from "../../auth/getFeedData";
import { zeroPad } from "../../lib/utilities";
import { checkLikeBadges } from "../../lib/badgeLogic";
import { decrementUserValue } from "../../auth/decrementUserValue";
import { incrementUserValue } from "../../auth/incrementUserValue";
import { getGermanDate } from "../../lib/utilities";

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    flexDirection: "column",
    alignSelf: "center",
  },
  avatar: {
    width: theme.spacing(5),
    height: theme.spacing(5),
    marginRight: theme.spacing(1),
  },
  date: {
    color: theme.palette.grey[600],
  },
  vertDivider: {
    backgroundColor: theme.palette.primary.main,
    width: "2px",
  },
  time: {
    color: theme.palette.grey[600],
  },
  container: {
    display: "flex",
    margin: theme.spacing(0, 2, 1),
  },
  divider: {
    width: "100%",
    margin: theme.spacing(2, 0),
  },
  duration: {
    color: theme.palette.grey[600],
    marginLeft: theme.spacing(0.3),
  },
  activityExercises: {
    display: "flex",
    flexDirection: "column",
    marginLeft: theme.spacing(1),
  },
  mono: {
    fontFamily: "'Roboto Mono', Inter, monospace",
    display: "inline-block",
    minWidth: 15,
  },
  number: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
  },
  loadButton: {
    margin: "0 auto",
    display: "block",
  },
  verifiedSVG: {
    marginLeft: theme.spacing(1),
  },
  info: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  infoIcon: {
    fontSize: "1.5rem",
    marginRight: theme.spacing(1),
  },
  center: {
    textAlign: "center",
    marginTop: "35vh",
  },
  containerFlexSpaceBetween: {
    display: "flex",
    margin: theme.spacing(0, 2, 1),
    justifyContent: "space-between",
  },
  likeButton: {
    fontSize: "1rem",
  },
  likeCount: {
    marginRight: theme.spacing(1),
  },
  liked: {
    color: theme.palette.primary.main,
  },
  scrollContainer: {
    marginBottom: theme.spacing(8),
  },
  repetition: {
    marginRight: theme.spacing(0.5),
  },
}));

function FeedActivity({ setBadgeContent }) {
  const classes = useStyles();
  const [feedData, setFeedData] = useState();

  const [workoutsData, setWorkoutsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadInitialFeedData = async () => {
      setIsLoading(true);

      const initialFeedData = await getInitialFeedData();

      setFeedData(initialFeedData);
      setIsLoading(false);
    };

    loadInitialFeedData();
  }, []);

  useEffect(() => {
    if (feedData) {
      setWorkoutsData(feedData.workoutsPerDay);
    }
  }, [feedData]);

  const handleLike = async (docId) => {
    const currentUserId = feedData.currentUser.id;
    let givenLikes = feedData.currentUser?.givenLikes ?? 0;
    const likedActivity = feedData.workoutsPerDay.find((workout) => workout.docId === docId);
    const index = feedData.workoutsPerDay.indexOf(likedActivity);
    let newWorkoutsPerDay = [...feedData.workoutsPerDay];
    let newLikedActivity;

    if (likedActivity.likedByUsers.includes(currentUserId)) {
      newLikedActivity = {
        ...likedActivity,
        likedByUsers: likedActivity.likedByUsers.filter((userId) => userId !== currentUserId),
      };

      await removeLike(docId, currentUserId);
      if (givenLikes > 0) {
        await decrementUserValue("givenLikes");
        givenLikes--;
      }
    } else {
      newLikedActivity = {
        ...likedActivity,
        likedByUsers: [...likedActivity.likedByUsers, currentUserId],
      };

      await like(docId, currentUserId);
      await incrementUserValue("givenLikes");

      const badgeContent = checkLikeBadges(givenLikes);
      givenLikes++;
      badgeContent && setBadgeContent(badgeContent);
    }

    newWorkoutsPerDay.splice(index, 1, newLikedActivity);

    setFeedData((prevState) => ({
      ...prevState,
      workoutsPerDay: newWorkoutsPerDay,
      currentUser: {
        ...prevState.currentUser,
        givenLikes,
      },
    }));
  };

  const renderNewActivities = () => {
    if (feedData && workoutsData) {
      const { workoutsPerDay, currentUser } = feedData;

      const activities = workoutsData.map((dayWorkout, idx) => {
        const { date, exercises, photoUrl, userName, likedByUsers } = dayWorkout;
        const liked = likedByUsers.includes(currentUser.id);

        return (
          <div key={idx}>
            <div className={classes.root}>
              <div className={classes.container}>
                <Avatar
                  className={classes.avatar}
                  alt={userName}
                  src={photoUrl}
                  referrerPolicy="no-referrer"
                />
                <div className={classes.header}>
                  <Typography variant="body2">{userName}</Typography>
                  <Typography variant="subtitle2">
                    {`${exercises.length} Übung${exercises.length > 1 ? "en" : ""}`}
                  </Typography>
                </div>
              </div>
            </div>
            <div className={classes.container}>
              <Divider orientation="vertical" flexItem className={classes.vertDivider} />
              <span className={classes.activityExercises}>
                {workoutsPerDay &&
                  exercises.map((workout, idx) => {
                    const duration = new Date(workout.duration * 1000).toISOString().substr(14, 5);
                    return (
                      <div key={idx}>
                        <Typography variant="caption" style={{ display: "flex" }}>
                          <span className={`${classes.number} ${classes.mono}`}>
                            {zeroPad(workout.stNr, 2)}
                          </span>
                          <span className={`${classes.repetition} ${classes.mono}`}>
                            {zeroPad(workout.repetition, 2)}
                          </span>
                          {workout.name}
                          <span className={classes.duration}> ({duration})</span>
                          {workout.verified && (
                            <img
                              src={verifiedSVG}
                              alt="verified svg"
                              height="15"
                              className={classes.verifiedSVG}
                            />
                          )}
                        </Typography>
                      </div>
                    );
                  })}
              </span>
            </div>
            <div className={classes.containerFlexSpaceBetween}>
              <Typography variant="caption" className={classes.date}>
                {getGermanDate(date, true)}
              </Typography>
              <div>
                <div className={classes.info}>
                  <Typography
                    variant="caption"
                    className={liked ? `${classes.liked} ${classes.likeCount}` : classes.likeCount}
                  >
                    {dayWorkout.likedByUsers.length}{" "}
                  </Typography>
                  {liked ? (
                    <AiFillHeart
                      onClick={() => handleLike(dayWorkout.docId)}
                      className={`${classes.likeButton} ${classes.liked}`}
                    />
                  ) : (
                    <AiOutlineHeart
                      onClick={() => handleLike(dayWorkout.docId)}
                      className={classes.likeButton}
                    />
                  )}
                </div>
              </div>
            </div>
            <Divider className={classes.divider} />
          </div>
        );
      });
      return activities;
    }
  };

  const fetchNextData = async () => {
    if (feedData) {
      const nextData = await getNextFeedData(feedData);

      setFeedData((prevState) => {
        return {
          ...prevState,
          workoutsPerDay: [...prevState.workoutsPerDay, ...nextData.workoutsPerDay],
          lastIndex: nextData.lastIndex,
          noNextData: nextData.noNextData,
        };
      });
    }
  };

  return (
    <div className={classes.scrollContainer}>
      {isLoading ? (
        <div className={classes.center}>
          <CircularProgress />
        </div>
      ) : (
        renderNewActivities()
      )}
      {/* If there are no workoutsPerDay, the Info is displayed */}
      {feedData?.workoutsPerDay.length === 0 && (
        <Typography
          variant="subtitle1"
          color="textSecondary"
          className={`${classes.info} ${classes.center}`}
        >
          <AiOutlineInfoCircle className={classes.infoIcon} /> Noch keine Aktivitäten
        </Typography>
      )}
      {/* If there are workoutsPerDay and next data, the button is displayed */}
      {!isLoading && feedData?.workoutsPerDay.length > 0 && !feedData.noNextData && (
        <Button
          variant="outlined"
          className={classes.loadButton}
          color="default"
          onClick={() => fetchNextData()}
        >
          Mehr laden
        </Button>
      )}
    </div>
  );
}

export default FeedActivity;
